import { isDevelopmentEnvironment } from "../../estada-config";
import KnxProductData from "../../KnxProductdata";
import determineActorComObjectFunction from "../../KnxProductDataTools/determineActorComObjectFunction";
import determineButtonComObjectFunction from "../../KnxProductDataTools/determineButtonComObjectFunction";
import Logger from "../../Logger";
import BaseModelMap from "../../models/BaseModelMap";
import Button from "../../models/Button";
import Channel, { ChannelDescriptionProperty } from "../../models/Channel";
import { DeviceType } from "../../models/ChannelType";
import Device from "../../models/Device";
import Signal from "../../models/Signal";
import SignalTypes from "../../models/SignalTypes";
import { isUiModeDeveloper } from "../../uiMode";

import Allocator from "./Allocator";
import { KnxChapterItem } from "./KnxChapterItem";
import KnxContext from "./KnxContext";
import { noItx } from "../../translationItx/itx";

const logger = new Logger();

export function determineComObjectFunction(
  device: Device,
  knxProductData: KnxProductData,
  isActor: boolean,
  signal: Signal,
) {
  try {
    signal.cleanDatapointType();

    const t = knxProductData.config.getSignalTypeMapping(signal.knxId);
    if (t) {
      signal.type = t;
      signal.determinedFromId = "manual"; // no itx
    } else {
      if (isActor) {
        let deviceType: DeviceType | undefined = undefined;

        if (device.deviceType != DeviceType.Unknown && device.deviceType != DeviceType.Generic) {
          deviceType = device.deviceType;
        }

        determineActorComObjectFunction(signal, undefined, deviceType);
      } else {
        determineButtonComObjectFunction(signal);
      }
    }
  } catch (ex) {
    logger.error("determineComObjectFunctions", ex);
  }
}

export function findSignalByNumber(signals: BaseModelMap<Signal>, number: string): Signal | undefined {
  var res: Signal | undefined;

  signals.forEach((signal) => {
    if (res == undefined && signal.number == number) {
      res = signal;
    }
  });

  return res;
}

export function calcValueWithBaseValue(props: {
  inputNumber: number;
  data: any;
  args: any;
  knxContext: KnxContext;
}): number {
  const { knxContext, data, args, inputNumber } = props;

  let result = inputNumber;
  const item = data;

  let baseNumberParameterId: string | undefined = undefined;
  if (item.a.BaseNumber) {
    baseNumberParameterId = item.a.BaseNumber;
  } else if (item.a.BaseValue) {
    baseNumberParameterId = item.a.BaseValue;
  }

  if (!baseNumberParameterId) {
    return inputNumber;
  }

  const AllocationSizePerIndex = parseInt(knxContext.getArgAllocatesByRefId(args, baseNumberParameterId) || "1");

  let fullrepeatindex = 0;

  var baseNumber = "";
  let allocator: Allocator | undefined;

  if (baseNumberParameterId) {
    const arg = knxContext.getArgByRefId(args, baseNumberParameterId);
    if (arg) {
      baseNumber = knxContext.getArgValueByRefId(args, baseNumberParameterId) || "";
      if (arg.a.AllocatorRefId) {
        allocator = Allocator.getAllocator(arg.a.AllocatorRefId);
        fullrepeatindex = allocator?.getCurrentIndex() || 0;
        baseNumber = allocator?.Start || "0";
      }
    }

    if (baseNumber == undefined) {
      logger.log(`transform_ComObjectProperties basenumber not found: ${baseNumberParameterId}`);
    } else {
      let b = parseInt(baseNumber);
      if (AllocationSizePerIndex) {
        b += fullrepeatindex * AllocationSizePerIndex; // repeatindex starts with 0
      }

      result = b + inputNumber;

      // logger.log(
      //   `ComObj: ${cor.a.FunctionText} ${number} (${parseInt(baseNumber)} ${repeatindex} ${AllocationSizePerIndex})`,
      // );
    }
  }

  return result;
}

function transform_ComObjectProperties(props: {
  instanceId: string;
  moduleId: string;
  data: any;
  dataRefRef: any;
  parentChannel: Channel;
  args: any;
  repeatindex?: number;
  params: any;
  title: string;
  nestedName: string;
  nestedText: string;
  refId: string;
  knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
  knxContext: KnxContext;
}): any {
  const {
    knxContext,
    data,
    dataRefRef,
    args,
    repeatindex,
    parentChannel,
    title,
    nestedName,
    params,
    instanceId,
    moduleId,
    refId,
  } = props;

  var { knxChannelDescriptionProperty } = props;

  // logger.log(`transform_ComObjectProperties ${channel?.name}`);

  if (data == undefined) {
    logger.error("data undefinded");
    return;
  }

  const cor = knxContext.deref(data);
  const corRefRef = dataRefRef;

  cor.__refId = refId;

  var baseNumber = "";
  const baseNumberParameterId = cor.a.BaseNumber;
  const AllocationSizePerIndex = parseInt(knxContext.getArgAllocatesByRefId(args, baseNumberParameterId) || "1");

  let fullrepeatindex = 0;

  let allocator: Allocator | undefined;
  if (baseNumberParameterId) {
    const arg = knxContext.getArgByRefId(args, baseNumberParameterId);
    if (arg) {
      baseNumber = knxContext.getArgValueByRefId(args, baseNumberParameterId) || "";
      if (arg.a.AllocatorRefId) {
        allocator = Allocator.getAllocator(arg.a.AllocatorRefId);
        fullrepeatindex = allocator?.getCurrentIndex() || 0;
        baseNumber = allocator?.Start || "0";
      }
    }

    if (baseNumber == undefined) {
      logger.log(`transform_ComObjectProperties basenumber not found: ${baseNumberParameterId}`);
    }
  }

  var number = cor.a.Number;
  if (baseNumber) {
    let b = parseInt(baseNumber);
    if (AllocationSizePerIndex) {
      b += fullrepeatindex * AllocationSizePerIndex; // repeatindex starts with 0
    }

    number = b + parseInt(cor.a.Number);

    // logger.log(
    //   `ComObj: ${cor.a.FunctionText} ${number} (${parseInt(baseNumber)} ${repeatindex} ${AllocationSizePerIndex})`,
    // );
  }

  const numberNew = calcValueWithBaseValue({
    knxContext,
    data,
    args,
    inputNumber: parseInt(cor.a.Number),
  });

  if (numberNew != number) {
    logger.error(`calcValueWithBaseValue: result is diffent: ${number} !== ${numberNew}`);
  }

  const functionText =
    knxContext.knxProductData.getTranslatedProperty("FunctionText", corRefRef) || corRefRef.a.FunctionText; // no itx
  let text = knxContext.knxProductData.getTranslatedProperty("Text", corRefRef) || corRefRef.a.Text;

  ////////////////////////////////////////////
  // engTranslation
  const functionTextEN =
    knxContext.knxProductData.getTranslatedProperty("FunctionText", corRefRef, true) || corRefRef.a.FunctionText; // no itx
  let textEN = knxContext.knxProductData.getTranslatedProperty("Text", corRefRef, true) || corRefRef.a.Text;

  /////////////////////////////////////////////////////////////////////////////////////////

  // var text0 = text;

  let TextParameterRefId = cor.a.TextParameterRefId; //  = "MD-3_P-1_R-1"

  const hasScript_mapChannelText = knxContext.knxProductData.hasScript_mapChannelText;
  if (!hasScript_mapChannelText && TextParameterRefId) {
    for (const [key, target] of knxContext.knxInstanceData.assigns) {
      if (target) {
        if (target.instanceId == instanceId && target.toId == TextParameterRefId) {
          TextParameterRefId = key;
          break;
        }
      }
    }
  }

  // if (TextParameterRefId != undefined) {
  //   knxContext.knxInstanceData.setReferenced(TextParameterRefId, instanceId);
  //   // knxContext.knxInstanceData.addReEvalId(TextParameterRefId, instanceId);
  //   // ta.addReEvalId(TextParameterRefId, instanceId);

  //   const xText0 = knxContext.getValue(TextParameterRefId, instanceId, false, true);
  //   if (xText0 == undefined) {
  //     const text0_Default = knxContext.knxProductData.getDefaultValueForParameter(
  //       knxContext.knxProductData?.globalMap.get(TextParameterRefId),
  //       instanceId,
  //     );
  //     if (text0_Default != undefined) {
  //       text0 = text0_Default;
  //     }
  //   } else {
  //     text0 = xText0;
  //   }

  const text0 = `<%${TextParameterRefId}%>`;

  /////////////////////////////////////////////////////////////////////////////////////////

  textEN = knxContext.substitutePlaceholders({
    input: textEN,
    args: args,
    repeatindex: repeatindex,
    params: params,
    title0: undefined,
    instanceId: instanceId,
    moduleId: moduleId,
  });

  const knxTextENForDetectionRaw =
    knxContext.knxProductData.getTranslatedProperty("Text", corRefRef, true) || corRefRef.a.Text;
  let knxTextENForDetection = knxContext.substitutePlaceholders({
    input: knxTextENForDetectionRaw,
    args: args,
    repeatindex: repeatindex,
    params: params,
    title0: undefined,
    instanceId: instanceId,
    moduleId: moduleId,
  });

  if (parentChannel) {
    const duplicateSignal = findSignalByNumber(parentChannel.signals, number);

    if (duplicateSignal) {
      logger.error(`duplicate signal found ${number} ${cor.a.Name}`, cor);
    } else {
      const signal = new Signal(
        {
          knxId: cor.__refId,
          instanceId: instanceId,
          a: cor.a,
          knxTextEN: textEN,
          knxTextENForDetection_Template: knxContext.translateToEnglish(knxTextENForDetection),
          knxTextENForDetectionRaw: knxContext.translateToEnglish(knxTextENForDetectionRaw),
          knxFunctionText: functionText,
          knxFunctionTextEN: knxContext.translateToEnglish(functionTextEN),
          knxFunctionTextOrg: cor.a.FunctionText,
          knxDatapointType: cor.a.DatapointType,
          knxNestedName: `${nestedName}|${cor.a.Name}`,

          knxRefId: refId,
          knxChannelId: parentChannel.knxChannelId,

          _name: text,
          number: `${number}`,
          type: cor.JeannieFunction,
        },
        parentChannel,
      );

      text = knxContext.substitutePlaceholders({
        input: text,
        args: args,
        repeatindex: repeatindex,
        params: params,
        title0: text0,
        instanceId: instanceId,
        moduleId: moduleId,
        signal: signal,
      });

      signal.name = text;

      if (knxContext.isActor && knxContext.isChannelIndependentBlock) {
        signal.isChannelIndependent = true;
      }

      knxContext.currentRawSceneData = undefined;

      knxContext.lastSignalNumber = Number(number);

      signal.a = cor.a;

      if (TextParameterRefId) {
        signal.knxChannelDescriptionProperty = {
          id: TextParameterRefId,
          instanceId: instanceId,
          moduleId: moduleId,
          xid: "",
        };
      }

      knxContext.currentChannelDescriptionProperty = undefined;
      signal.knxChannelTypeProperty = knxContext.currentChannelTypeProperty;

      knxContext.currentChannelTypeProperty = undefined;

      const isSceneNumberSignal =
        cor.a.DatapointType?.startsWith("DPST-18-") || cor.a.DatapointType?.startsWith("DPST-17-");
      if (isSceneNumberSignal) {
        if (!knxContext.isActor) {
          signal.knxSceneNumberProperty = knxContext.currentKnxButtonSceneNumberProperty;
          knxContext.currentKnxButtonSceneNumberProperty = undefined;
          knxContext.lastSceneSignal = signal;
        }
      }

      knxContext.currentKnxButtonSceneNumberProperty = undefined;
      // parentChannel.knxChannelTypeProperty = undefined
      determineComObjectFunction(
        knxContext.device,
        knxContext.knxProductData,
        (knxContext.isActor && knxContext.device.deviceType != DeviceType.Lock) ||
          (knxContext.device.isButton && (knxContext.device as Button).isDecentralActor),
        signal,
      );

      if (knxContext.device.deviceType == DeviceType.Lock) {
        if (signal.type == SignalTypes.Switch) signal.type = SignalTypes.Lock;
      }

      if (signal.type == undefined) {
        logger.error("EEEEEEEEEEEEEEEEEEEEEEEEEEE");
        signal.type = SignalTypes.Unknown;
      }

      parentChannel.signals.addWithoutUpdate(signal);
      knxContext.xSignals.push(signal);
      parentChannel.active = true;
    }
  }
  if (isDevelopmentEnvironment() && isUiModeDeveloper()) {
    const debugTr = (
      <div className="knxDebugView">
        {baseNumber ? (
          <div className="estataColumn">
            <div>
              {noItx("baseNumber=")}
              {baseNumber} {noItx("repeatindex=")}
              {repeatindex} {noItx("AllocationSizePerIndex=")}
              {AllocationSizePerIndex}
            </div>
            <div>
              {noItx("ComObj:")} {cor.a.Number} {"=>"} {number}
            </div>
          </div>
        ) : (
          <div>
            {noItx("ComObj:")} {number}
          </div>
        )}
        <div>
          {noItx("refId=")}
          {cor.a.RefId} {noItx("fun=")}
          {functionTextEN} {noItx("dpst=")}
          {cor.a.DatapointType}{" "}
        </div>
        <div>
          {noItx("txtEN=")}
          {knxTextENForDetection}
        </div>
        <div>
          {noItx("txtRaw=")}
          {knxTextENForDetectionRaw}
        </div>
        <div>
          {noItx("textParameterId=")}
          {cor.a.TextParameterRefId}{" "}
        </div>
      </div>
    );

    const lastFlatItem = knxContext.flatItems.length
      ? knxContext.flatItems[knxContext.flatItems.length - 1]
      : undefined;

    if (lastFlatItem) {
      knxContext.flatItems.push({
        name: text,
        type: "P",
        order: lastFlatItem.order + 0.1,
        elm: debugTr,
        indent: lastFlatItem.indent,
        id: cor.__refId,
      });
    }

    return debugTr;
  }
}

export default function ComObjectRefRef(props: {
  knxContext: KnxContext;
  parentKnxChapter: KnxChapterItem | undefined;
  instanceId: string;
  moduleId: string;
  chapterId: string;
  data: any;
  parentChannel: Channel;
  args: any;
  repeatindex?: number;
  params: any;
  title: string;
  text: string | undefined;
  nestedName: string;
  nestedText: string;
  knxChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
}) {
  const { data, parentKnxChapter } = props;

  if (isDevelopmentEnvironment() && parentKnxChapter) {
    parentKnxChapter.comObjectList.push(data);
  }

  return transform_ComObjectProperties({
    ...props,
    data: props.knxContext.deref(data),
    dataRefRef: data,
    refId: data.a.RefId,
  });
}
